/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useQuickList.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for managing quick list modal functionality for Role Profiles.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useMemo, useRef } from 'react';
import { useAuthStore } from '../../../store/auth';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';
import { fetchQuickListRoleProfiles } from '../api/roleProfilesApi';
import { QuickListRoleProfile } from '../types';

export function useQuickList(showQuickList: boolean) {
  const { user: currentUser } = useAuthStore();
  const { t } = useI18n();
  const { showError } = useNotifications();
  const tenantSlug = currentUser?.tenant?.slug || t('profiles.defaultTenant');

  const [quickListSearch, setQuickListSearch] = useState('');
  const [quickListProfiles, setQuickListProfiles] = useState<QuickListRoleProfile[]>([]);
  const [isQuickListLoading, setIsQuickListLoading] = useState(false);
  const quickListLoadingRef = useRef(false);

  const tRef = useRef(t);
  const showErrorRef = useRef(showError);

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  useEffect(() => {
    showErrorRef.current = showError;
  }, [showError]);

  const loadQuickList = async () => {
    if (quickListLoadingRef.current) return;
    
    quickListLoadingRef.current = true;
    setIsQuickListLoading(true);
    try {
      const profiles = await fetchQuickListRoleProfiles(quickListSearch, tenantSlug, currentUser?.id);
      setQuickListProfiles(profiles);
    } catch (error) {
      showErrorRef.current(error?.message || tRef.current('profiles.errors.loadFailed'));
      setQuickListProfiles([]);
    } finally {
      setIsQuickListLoading(false);
      quickListLoadingRef.current = false;
    }
  };

  useEffect(() => {
    if (showQuickList && quickListProfiles.length === 0 && !quickListLoadingRef.current) {
      loadQuickList();
    }
  }, [showQuickList, quickListProfiles.length]);

  const filteredQuickList = useMemo(() => {
    const profiles = quickListProfiles.filter(p => p.id != null);
    if (!quickListSearch.trim()) {
      return profiles;
    }
    const searchLower = quickListSearch.toLowerCase();
    return profiles.filter(
      (profile) =>
        profile.name?.toLowerCase().includes(searchLower) ||
        profile.description?.toLowerCase().includes(searchLower)
    );
  }, [quickListProfiles, quickListSearch]);

  return {
    quickListSearch,
    setQuickListSearch,
    filteredQuickList,
    isQuickListLoading,
  };
}

